import { useState, useEffect, useRef } from "react";

const REGISTERED_FILTER_KEYS = new Set([
  "procurement_pipeline_filters",
  "purchase_orders_filters",
  "indents_filters",
  "deliveries_filters",
  "mirs_filters",
  "other_pos_filters",
  "po_by_project_filters",
  "purchase_cards_status",
  "purchase_cards_project",
  "purchase_cards_wo",
  "purchase_cards_search",
  "purchase_cards_page",
  "contractor_delivery_page",
  "contractor_delivery_search",
  "contractor_jmr_filters",
  "vendor_jmr_filters",
  "vendor_work_orders_filters",
  "vendor_wo_billing_filters",
  "work_orders_cards_filters",
  "work_orders_cards_page",
  "work_orders_filters",
  "work_orders_page",
  "contractual_work_orders_filters",
  "boq_amend_project",
  "boq_amend_wo",
  "boq_amend_search",
  "boq_amend_from",
  "boq_amend_to",
  "boq_amend_page",
  "measurement_sheets_params",
  "dprs_filters",
  "hindrance_register_filters",
  "projects_filters",
  "projects_page",
  "commercial_dashboard_project",
  "milestone_project",
  "milestone_wo",
  "milestone_status",
  "milestone_alert",
  "milestone_cat",
  "milestone_search",
  "risk_project",
  "risk_status",
  "risk_sev",
  "risk_cat",
  "risk_search",
  "accounts_status",
  "accounts_project",
  "accounts_wo",
  "accounts_page",
  "billing_cards_project",
  "billing_cards_wo",
  "billing_cards_search",
  "billing_cards_page",
  "billings_filters",
  "po_pay_project",
  "po_pay_wo",
  "po_pay_search",
  "po_pay_status",
  "po_pay_page",
  "vendor_pay_project",
  "vendor_pay_wo",
  "vendor_pay_search",
  "vendor_pay_status",
  "vendor_pay_page",
  "contractor_pay_project",
  "contractor_pay_wo",
  "contractor_pay_search",
  "contractor_pay_status",
  "contractor_pay_page",
  "other_po_pay_search",
  "other_po_pay_status",
  "gst_recon_filters",
  "debit_list_filters",
  "office_expense_filters",
  "hr_crm_status",
  "hr_crm_project",
  "hr_crm_wo",
  "hr_crm_search",
  "hr_crm_page",
  "staff_all_attendance_filters",
  "staff_self_attendance_filters",
  "staff_att_month",
  "staff_att_year",
  "staff_att_search",
  "attendance_wo",
  "attendance_month",
  "attendance_year",
  "attendance_search",
  "staff_sal_search",
  "staff_sal_month",
  "staff_sal_year",
  "salary_month",
  "salary_year",
  "salary_search",
  "salary_status",
  "payroll_search",
  "reimbursement_search",
  "reimbursement_status",
  "reimbursement_category",
  "reimbursement_date_from",
  "reimbursement_date_to",
  "employee_crm_search",
  "client_tickets_filters",
  "pending_approvals_search",
  "pending_approvals_module",
  "pending_approvals_page",
  "admin_task_kanban_filters",
  "task_filters",
  "task_reporting_summary_filters",
  "teams_filters",
  "operations_status",
  "operations_project",
  "operations_wo",
  "operations_search",
  "operations_page",
  "warehouse_filters",
  "inventory_pos_filters",
  "inventory_stock_ledger_filters",
  "inventory_stock_summary_filters",
  "equipment_search",
  "equipment_status",
  "equipment_cat",
  "equipment_page",
  "qc_list_filters",
  "qc_ncr_filters",
  "qc_report_filters",
  "wir_list_filters",
  "safety_incidents_filters",
  "safety_checklists_filters",
  "safety_checklist_filters",
  "safety_report_filters",
  "statutory_approvals_filters",
  "handover_list_filters",
  "hoto_wo",
  "hoto_status",
  "hoto_search",
  "vendors_filters",
  "vendor_categories_filters",
  "clients_page",
  "perm_search",
  "perm_page",
  "roles_page",
  "documents_filters",
  "tc_list_filters",
  "tds_list_filters",
  "notif_page",
  "notif_module",
  "notif_search",
  "product_master_search",
]);

/**
 * clearAllPersistentFilters
 * Clears all saved filter & pagination states from sessionStorage and notifies active components.
 * Called when user explicitly navigates to a tab from the sidebar menu.
 */
export function clearAllPersistentFilters() {
  try {
    REGISTERED_FILTER_KEYS.forEach((key) => {
      sessionStorage.removeItem(key);
    });
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const k = sessionStorage.key(i);
      if (
        k &&
        (
          k.endsWith("_filters") ||
          k.endsWith("_page") ||
          k.endsWith("_search") ||
          k.endsWith("_status") ||
          k.endsWith("_project") ||
          k.endsWith("_wo") ||
          k.endsWith("_params")
        )
      ) {
        sessionStorage.removeItem(k);
      }
    }
  } catch (_) {}

  // Broadcast event so currently mounted components reset immediately
  try {
    window.dispatchEvent(new CustomEvent("clear-app-filters"));
  } catch (_) {}
}

/**
 * usePersistentState
 * Persists any single state value in sessionStorage.
 */
export function usePersistentState(storageKey, defaultValue) {
  REGISTERED_FILTER_KEYS.add(storageKey);

  const [state, setState] = useState(() => {
    try {
      const saved = sessionStorage.getItem(storageKey);
      if (saved !== null && saved !== undefined) {
        return JSON.parse(saved);
      }
    } catch (_) {}
    return typeof defaultValue === "function" ? defaultValue() : defaultValue;
  });

  const keyRef = useRef(storageKey);
  keyRef.current = storageKey;
  const defRef = useRef(defaultValue);
  defRef.current = defaultValue;

  useEffect(() => {
    try {
      if (keyRef.current) {
        sessionStorage.setItem(keyRef.current, JSON.stringify(state));
      }
    } catch (_) {}
  }, [state]);

  // Listen for sidebar click reset event
  useEffect(() => {
    const handleClear = () => {
      const def = typeof defRef.current === "function" ? defRef.current() : defRef.current;
      setState(def);
    };
    window.addEventListener("clear-app-filters", handleClear);
    return () => window.removeEventListener("clear-app-filters", handleClear);
  }, []);

  return [state, setState];
}

/**
 * usePersistentFilters
 * A drop-in replacement for `useState(defaultFilters)` in listing pages.
 * Automatically saves filter state to sessionStorage and restores it on remount
 * (e.g., when navigating to a detail/preview/edit page and coming back).
 */
export function usePersistentFilters(storageKey, defaultFilters) {
  REGISTERED_FILTER_KEYS.add(storageKey);

  const [filters, setFilters] = useState(() => {
    const defaults = typeof defaultFilters === "function" ? defaultFilters() : defaultFilters;
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        return { ...defaults, ...parsed };
      }
    } catch (_) {}
    return defaults;
  });

  const keyRef = useRef(storageKey);
  keyRef.current = storageKey;
  const defRef = useRef(defaultFilters);
  defRef.current = defaultFilters;

  useEffect(() => {
    try {
      if (keyRef.current) {
        sessionStorage.setItem(keyRef.current, JSON.stringify(filters));
      }
    } catch (_) {}
  }, [filters]);

  const resetFilters = (overrideDefaults) => {
    try {
      if (keyRef.current) {
        sessionStorage.removeItem(keyRef.current);
      }
    } catch (_) {}
    const defaults = typeof defRef.current === "function" ? defRef.current() : defRef.current;
    setFilters(overrideDefaults || defaults);
  };

  // Listen for sidebar click reset event
  useEffect(() => {
    const handleClear = () => {
      const defaults = typeof defRef.current === "function" ? defRef.current() : defRef.current;
      setFilters(defaults);
    };
    window.addEventListener("clear-app-filters", handleClear);
    return () => window.removeEventListener("clear-app-filters", handleClear);
  }, []);

  return [filters, setFilters, resetFilters];
}

export default usePersistentFilters;
